import { tokenClaims } from './entity-claim';
import { refreshTokenFresh } from './auth-token';
import { scopeRootOfClaims } from '../util/jwt';
import { createBoundedRetry } from './bounded-retry';

/**
 * Is a disagreeing root in storage a real move? Split out of
 * `scope-reconciliation.ts` (review of 291019f0, item 8).
 *
 * <p>The token writer refuses to replace a newer token for the same user
 * (`processTokenData`), so a disagreeing token in storage is normally the
 * newest and is believed with no refresh — N open tabs do not each refresh on
 * one `storage` event. Two cases get ONE verifying refresh: a token issued no
 * later than the newest one that agreed with this tab (the writer lets an equal
 * issue second through), and a tab that has never seen one agree.
 *
 * <p>A verifying refresh that FAILS confirms nothing (review of 291019f0, item
 * 3): the stored token is exactly what was in doubt. It is retried with a
 * bounded backoff (review of 24a2f326, item 5) — a background tab gets no other
 * trigger — and by any trigger in between; never immediately, which on a
 * Keycloak outage would loop.
 */

/** The moved-to root this tab has accepted as real, or null. */
let confirmedRoot: string | null = null;
let verification: Promise<void> | null = null;
/** `iat` of the newest stored token this tab has seen agree with its stamp. */
let agreeingIat: number | null = null;
const retry = createBoundedRetry();

/** The stored token (issued at `iat`) agrees with this tab's stamp. */
export const noteAgreeingToken = (iat: number | null): void => {
  if (iat !== null && (agreeingIat === null || iat > agreeingIat)) agreeingIat = iat;
};

/**
 * Is the move to `movedTo`, on a stored token issued at `iat`, real? True when
 * already confirmed or issued after the newest agreeing token. Otherwise starts
 * one verifying refresh (single-flight) and answers false; `onSettled` runs
 * when that refresh succeeds, or after a backoff when it fails.
 */
export const isMoveConfirmed = (
  movedTo: string,
  iat: number | null,
  onSettled: () => void,
): boolean => {
  if (confirmedRoot === movedTo) return true;
  if (agreeingIat !== null && iat !== null && iat > agreeingIat) {
    confirmedRoot = movedTo;
    return true;
  }
  if (!verification) {
    verification = refreshTokenFresh().then(
      () => {
        verification = null;
        retry.reset();
        // Minted from the attributes as they are now: current by construction.
        confirmedRoot = scopeRootOfClaims(tokenClaims());
        onSettled();
      },
      () => {
        verification = null;
        retry.schedule(onSettled);
      },
    );
  }
  return false;
};

/** The move this tab has accepted as real, or null. */
export const confirmedMoveTo = (): string | null => confirmedRoot;

/** The tab agrees with the token again, or is reloading: forget the move. */
export const forgetConfirmedMove = (): void => {
  confirmedRoot = null;
  retry.reset();
};

/** Test seam. */
export const __resetMoveTrackerForTests = (): void => {
  confirmedRoot = null;
  verification = null;
  agreeingIat = null;
  retry.reset();
};
